/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IDlvryQuestbackData } from './dlvry-questback.data';
import { IDlvryQuestbackProps } from './dlvry-questback.props.autogenerated';
import { setLastSurveyDateAsync } from './actions/set-last-survey-date';

export interface IDlvryQuestbackViewProps extends IDlvryQuestbackProps<IDlvryQuestbackData> {
    baseClassName: string;
    popupId: string;
    popupClassName: string;
    modalContentClassName: string;
    modalContentCloseButton: React.ReactElement;
    questBackIFrame: React.ReactNode;
}

/**
 *
 * DlvryQuestback component
 * @extends {React.PureComponent<IDlvryQuestbackProps<IDlvryQuestbackData>>}
 */
class DlvryQuestback extends React.PureComponent<IDlvryQuestbackProps<IDlvryQuestbackData>> {
    private _baseClassName: string = 'dlvry-questback';
    private _popupId: string = `${this._baseClassName}__questback-popup`;

    public componentDidMount(): void {
        const { context, config } = this.props;
        const shouldShow = this.shouldShow();

        if (window && !window.resetSurvey) {
            window.resetSurvey = async () => {
                void setLastSurveyDateAsync({ callerContext: this.props.context.actionContext }, new Date(0));
            };
        }

        if (config.disableModule || !context.request.user.isAuthenticated || context.request.params.isEditor || !shouldShow) {
            return;
        }

        const oboCustomerAccountNumber: { can: string | undefined } = Msdyn365.getCustomerAccountNumber(context.request) as any;

        // Don't show popup yet if logged in as OBO, but no account has been selected yet
        if (context.request.user.isOBORequest && !oboCustomerAccountNumber.can) {
            return;
        }

        setTimeout(async () => {
            const popup = document.getElementById(this._popupId);

            // Wait for consent to be hidden before showing the popup
            while (window.consentVisible) {
                await new Promise(resolve => setTimeout(resolve, 250));
            }

            if (popup) {
                popup.style.display = 'block';
                await this.setLastSurveyDate();
            }
        }, 2000);
    }

    public render(): JSX.Element | null {
        const { config, context, resources } = this.props;

        if (config.disableModule) {
            return null;
        }

        const viewProps: IDlvryQuestbackViewProps = {
            ...this.props,
            baseClassName: this._baseClassName,
            popupId: this._popupId,
            popupClassName: `${this._baseClassName}__modal`,
            modalContentClassName: `${this._baseClassName}__modal-content`,
            modalContentCloseButton: (
                <span className={`${this._baseClassName}__modal-content__close-button`} onClick={this.closePopup}>&times;</span>
            ),
            questBackIFrame: context.request.user.isAuthenticated
                ? <iframe src={this.populateCustomerInformation(config.questBackURL)} title={resources.questBackTitle} />
                : undefined
        };

        return this.props.renderView(viewProps);
    }

    private closePopup = (): void => {
        const popup = document.getElementById(this._popupId);
        if (popup) {
            popup.style.display = 'none';
        }
    };

    private populateCustomerInformation = (questBackURL: string | undefined): string | undefined => {
        if (!questBackURL) {
            return questBackURL;
        }

        const { context } = this.props;
        const { customerInformation, organizationInformation } = this.props.data;
        const url = new URL(questBackURL);

        if (organizationInformation.result?.AccountNumber) {
            url.searchParams.append('Kundenummer', organizationInformation.result.AccountNumber);
        }


        if (customerInformation.result?.Phone) {
            url.searchParams.append('Telefonnummer', customerInformation.result.Phone);
        }

        if (customerInformation.result?.Name) {
            url.searchParams.append('Brukernavn', customerInformation.result.Name);
        }

        if (organizationInformation.result?.Email) {
            url.searchParams.append('EpostOrg', organizationInformation.result.Email);
        }

        if (customerInformation.result?.Email) {
            url.searchParams.append('EpostKunde', customerInformation.result.Email);
        }

        url.searchParams.append('Grossist', context.request.apiSettings.oun);

        return url.toString();
    };

    private setLastSurveyDate = async (): Promise<void> => {
        await setLastSurveyDateAsync({ callerContext: this.props.context.actionContext }, new Date());
    };

    private shouldShow = (): boolean => {
        const {
            data: { customerInformation, organizationInformation }
        } = this.props;

        const disableCustomerSurvey = organizationInformation.result?.Attributes?.find(x => x.Name === 'DisableCustomerSurvey')?.AttributeValue?.BooleanValue;

        if (disableCustomerSurvey) {
            return false;
        }

        let lastSurveyDate = customerInformation.result?.ExtensionProperties?.find(x => x.Key === 'LASTSURVEYDATE')?.Value?.DateTimeOffsetValue;
        const dateToCheckAgainst = new Date();

        if (lastSurveyDate === undefined) {
            // Should never be undefined so if it is, assume the backend is not working
            return false;
        }

        lastSurveyDate = new Date(lastSurveyDate);
        dateToCheckAgainst.setDate(dateToCheckAgainst.getDate() - 7);

        return !lastSurveyDate || lastSurveyDate < dateToCheckAgainst;
    };
}

export default DlvryQuestback;
